import { useState } from 'react'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { queryClient, trpc, trpcClient } from '@/lib/trpc'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import type { ConfigModalProps, IntegrationProvider } from '../types'
import { IntegrationIcon } from '../icons'
import {
  createOAuthConnectLifecycle,
  openOAuthConnectPopup,
  type OAuthConnectStatus,
} from '../oauth-connect-lifecycle'

async function invalidateM365Queries() {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
    }),
    queryClient.invalidateQueries({
      queryKey: trpc.integrations.getConnectedProviderKeys.queryKey(),
    }),
    queryClient.invalidateQueries({
      queryKey: trpc.integrations.health.list.queryKey(),
    }),
    queryClient.invalidateQueries({
      queryKey: trpc.integrations.getCapabilityStatuses.queryKey(),
    }),
  ])
}

async function authorizeM365(
  onFinish?: (status: OAuthConnectStatus) => void
) {
  const baseWebUrl = import.meta.env.VITE_WEB_URL ?? window.location.origin
  const redirectUrl = `${baseWebUrl}/auth-callback/M365`
  const result = await trpcClient.integrations.initM365Auth.mutate({ redirectUrl })
  await new Promise<void>((resolve, reject) => {
    openOAuthConnectPopup({
      authUrl: result.authUrl,
      windowName: 'm365-oauth',
      provider: 'M365',
      onSuccess: () => resolve(),
      onError: (message) => reject(new Error(message)),
      onPopupBlocked: () =>
        reject(new Error('OAuth popup was blocked. Allow popups and try again.')),
      onFinish: (status) => {
        if (status !== 'success') onFinish?.(status)
        if (status === 'closed') {
          reject(new Error('Authorization window was closed.'))
        }
      },
    })
  })
  await invalidateM365Queries()
}

function M365ConfigModal({
  open,
  onOpenChange,
  onComplete,
}: ConfigModalProps) {
  const [isUpdating, setIsUpdating] = useState(false)
  const handleUpdateAccess = async () => {
    setIsUpdating(true)
    try {
      await authorizeM365()
      toast.success('Microsoft Entra ID access updated')
      onOpenChange(false)
      onComplete()
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update Microsoft Entra ID access'
      )
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[480px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <IntegrationIcon type='M365' />
            Manage Microsoft Entra ID
          </DialogTitle>
          <DialogDescription>
            Microsoft Entra ID, Teams, and SharePoint use the same Microsoft 365
            delegated OAuth authorization.
          </DialogDescription>
        </DialogHeader>

        <div className='px-6 py-2'>
          <div className='rounded-sm border border-border bg-status-green-bg px-3 py-2 text-[13px] text-status-green'>
            Connected via delegated OAuth. Update access by authorizing
            Microsoft 365 again to refresh the delegated permissions shared by
            all three integrations. No client secret is required.
          </div>
        </div>

        <DialogFooter>
          <Button
            variant='ghost'
            onClick={() => onOpenChange(false)}
            disabled={isUpdating}
          >
            Close
          </Button>
          <Button
            variant='outline'
            onClick={handleUpdateAccess}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin' />
                Authorizing...
              </>
            ) : (
              'Update access'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export const M365Provider: IntegrationProvider = {
  id: 'M365',
  name: 'Microsoft Entra ID',
  description: 'Scan Microsoft Entra ID identity configuration: Conditional Access, MFA, privileged roles, guest access, and app consent.',
  Icon: () => <IntegrationIcon type="M365" />,
  ConfigModal: M365ConfigModal,

  handleConnect: async ({ onSuccess, onFinish }) => {
    const lifecycle = createOAuthConnectLifecycle(onFinish)
    try {
      await authorizeM365((status) => lifecycle.finish(status))
      toast.success('Microsoft Entra ID connected')
      await onSuccess?.({ provider: 'M365' })
      lifecycle.finish('success')
    } catch (error) {
      console.error('Error initiating M365 OAuth:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to start M365 OAuth. Please try again.'
      )
      lifecycle.finish('error')
    }
  },

  onConnectComplete: async ({ code, state }) => {
    return trpcClient.integrations.completeM365Auth.mutate({ code, state })
  },

  handleDisconnect: async ({ integrationId }) => {
    try {
      await trpcClient.integrations.deleteIntegration.mutate({ integrationId })
      return { success: true }
    } catch (error) {
      console.error('Error disconnecting M365:', error)
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
    }
  },
}
